<template>
  <div v-omr-click-outside="closeDropDown" @keydown.esc="closeDropDown">
    <button data-testid="sidebar-side-menu-toggle" class="block w-full" @click.stop="toggleDropDown">
      <div class="cursor-pointer rounded-sm text-subtle dark-theme hover:state-layer-neutral">
        <div class="flex items-center justify-between rounded-sm text-subtle" :class="{ 'router-link-active': isActive }">
          <span class="flex max-w-[169px] items-center py-3" :class="classes">
            <slot name="prependLabel" />
            <p class="overflow-hidden text-ellipsis whitespace-nowrap">{{ props.label }}</p>
          </span>
          <div class="py-2 pr-4 text-right">
            <ChevronRightIcon class="lucide-icon-s" />
          </div>
        </div>
      </div>
    </button>

    <div
      v-if="isDropDownVisible"
      data-testid="sidebar-side-menu-dropdown"
      class="absolute top-0 left-58 h-full w-45 cursor-auto overflow-y-auto bg-surface-elevation-3 px-2 text-left"
    >
      <div class="flex items-center justify-between">
        <div class="flex flex-col gap-y-2 py-3 pl-2 text-main laptop:pt-6 laptop:pb-8">
          <slot name="prependDropDownLabel" />
          <h3 class="text-headline-xs">{{ props.label }}</h3>
        </div>
        <button
          v-if="!forceOpenActive"
          aria-label="close"
          data-testid="sidebar-side-menu-close"
          class="omr-focus-outline"
          @click="closeDropDown"
        >
          <XIcon class="lucide-icon-s cursor-pointer text-main" />
        </button>
      </div>
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ChevronRightIcon, XIcon } from '@ramp106/omrjs-core-ui/icons'
import { computed, ref } from 'vue'
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'

import { ClickOutside } from '@/directives/clickOutside'

const props = defineProps<{
  label: string
  to: RouteLocationRaw
  nestLevel?: 0 | 1 | 2
  sideBarUrls: Array<string>
  excludeRoutes?: Array<string>
  disableAutoToggle?: boolean
  forceOpen?: boolean
}>()

const router = useRouter()
const route = useRoute()

const vOmrClickOutside = ClickOutside
const isActive = computed(() => {
  // If current route is in excludeRoutes, this menu should not be active
  if (props.excludeRoutes && route.name && props.excludeRoutes.includes(route.name as string)) {
    return false
  }

  return props.sideBarUrls.some((item) => route.path === item || route.path.startsWith(`${item}/`))
})

const dropDownVisible = ref(false)
const forceOpenActive = computed(() => Boolean(props.forceOpen && isActive.value))
const isDropDownVisible = computed(() => forceOpenActive.value || dropDownVisible.value)

const toggleDropDown = () => {
  if (forceOpenActive.value) {
    if (!isActive.value) {
      router.push(props.to)
    }
    return
  }

  if (props.forceOpen && !isActive.value) {
    router.push(props.to)
    return
  }

  if (props.disableAutoToggle && !isActive.value) {
    router.push(props.to)
    return
  }

  dropDownVisible.value = !dropDownVisible.value

  if (dropDownVisible.value && !isActive.value) {
    router.push(props.to)
  }
}

const closeDropDown = () => {
  if (forceOpenActive.value) return
  dropDownVisible.value = false
}

const classes = computed(() => {
  switch (props.nestLevel) {
    case 1:
      return 'text-body-s pl-9 pr-5'
    case 2:
      return 'text-body-s pl-14 pr-5'
    default:
      return 'text-body-m pl-5 pr-2'
  }
})
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

.router-link-active {
  @apply bg-primary-alpha-strongest text-main;
}
</style>
